/**
 * 🎯 ThemeSelector P1.0 — Dropdown de sélection de thème
 * 
 * S'intègre dans le bouton "Idées thèmes" existant.
 * Mode libre uniquement. ZÉRO impact certifiant.
 * 
 * COMPORTEMENT MOA:
 * - Dropdown au lieu de chips
 * - Thème pris en compte APRÈS l'ouverture d'Owen (pas au START)
 * - Modifiable en cours de session
 * - Owen ne verbalise JAMAIS le thème
 */

import React, { useState, useEffect, useRef } from 'react';
import { Lightbulb, ChevronDown, Check, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ThemeSelector = ({ 
  onThemeSelect, 
  selectedTheme, 
  disabled = false,
  variant = 'button' // 'button' | 'inline'
}) => {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Charger les thèmes depuis l'API
  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const response = await fetch(`${API_URL}/api/coach/themes`);
        if (!response.ok) throw new Error('Erreur chargement thèmes');
        const data = await response.json();
        setThemes(data);
      } catch (err) {
        console.error('Erreur chargement thèmes:', err);
        // Fallback avec thèmes par défaut si API indisponible
        setThemes([
          { id: '1', slug: 'leadership', label: 'Leadership', category: 'professional' },
          { id: '2', slug: 'communication', label: 'Communication', category: 'professional' },
          { id: '3', slug: 'feedback', label: 'Feedback', category: 'professional' },
          { id: '4', slug: 'delegation', label: 'Délégation', category: 'professional' },
          { id: '5', slug: 'decision', label: 'Prise de décision', category: 'professional' },
          { id: '6', slug: 'confiance', label: 'Confiance', category: 'personal' },
          { id: '7', slug: 'motivation', label: 'Motivation', category: 'personal' },
          { id: '8', slug: 'relations', label: 'Relations', category: 'personal' },
          { id: '9', slug: 'clarte', label: 'Clarté mentale', category: 'personal' },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchThemes();
  }, []);

  // Fermer le dropdown si clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Grouper par catégorie
  const professionalThemes = themes.filter(t => t.category === 'professional');
  const personalThemes = themes.filter(t => t.category === 'personal');

  const handleThemeSelect = (theme) => {
    if (disabled) return;
    
    // Si même thème, désélectionner
    if (selectedTheme?.id === theme.id || selectedTheme?.slug === theme.slug) {
      onThemeSelect(null);
    } else {
      onThemeSelect(theme);
    }
    setIsOpen(false);
  };

  const clearSelection = (e) => {
    e.stopPropagation();
    onThemeSelect(null);
  };

  if (loading) {
    return (
      <button 
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-full animate-pulse"
        disabled
      >
        <Lightbulb size={16} />
        <span>Idées thèmes</span>
      </button>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bouton trigger */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-full
          transition-all duration-200 border-2
          ${selectedTheme 
            ? 'bg-amber-100 text-amber-700 border-amber-400 shadow-sm' 
            : 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100 hover:border-blue-300'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        data-testid="theme-selector-button"
      >
        <Lightbulb size={16} className={selectedTheme ? 'text-amber-500' : ''} />
        <span>
          {selectedTheme ? selectedTheme.label : 'Idées thèmes'}
        </span>
        {selectedTheme ? (
          <button
            onClick={(e) => {
              if (disabled) {
                e.stopPropagation();
                return;
              }
              clearSelection(e);
            }}
            disabled={disabled}
            className={`ml-1 p-0.5 rounded-full transition-colors ${
              disabled ? 'cursor-not-allowed' : 'hover:bg-amber-200'
            }`}
            data-testid="clear-theme-btn"
          >
            <X size={14} />
          </button>
        ) : (
          <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden"
          data-testid="theme-dropdown"
        >
          {/* Header */}
          <div className="px-4 py-2 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-600">
              Choisir un thème d'échange
            </p>
          </div>

          {/* 🔧 FIX MOA: Container scrollable pour mobile */}
          <div className="max-h-64 overflow-y-auto">
            {/* Thèmes professionnels */}
            {professionalThemes.length > 0 && (
              <div className="p-2">
                <p className="px-2 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  Professionnel
                </p>
                {professionalThemes.map(theme => (
                  <ThemeOption
                    key={theme.id || theme.slug}
                    theme={theme}
                    isSelected={selectedTheme?.slug === theme.slug}
                    onClick={() => handleThemeSelect(theme)}
                  />
                ))}
              </div>
            )}

            {/* Séparateur */}
            {professionalThemes.length > 0 && personalThemes.length > 0 && (
              <div className="border-t border-gray-100" />
            )}

            {/* Thèmes personnels */}
            {personalThemes.length > 0 && (
              <div className="p-2">
                <p className="px-2 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  Personnel
                </p>
                {personalThemes.map(theme => (
                  <ThemeOption
                    key={theme.id || theme.slug}
                    theme={theme}
                    isSelected={selectedTheme?.slug === theme.slug}
                    onClick={() => handleThemeSelect(theme)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
            <p className="text-xs text-gray-500 text-center">
              Owen s'adaptera à ton choix
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Option de thème dans le dropdown
 */
const ThemeOption = ({ theme, isSelected, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center justify-between px-3 py-2 rounded-lg text-left
        transition-colors duration-150
        ${isSelected 
          ? 'bg-amber-100 text-amber-700' 
          : 'hover:bg-gray-100 text-gray-700'
        }
      `}
      data-testid={`theme-option-${theme.slug}`}
    >
      <span className="text-sm font-medium">{theme.label}</span>
      {isSelected && <Check size={16} className="text-amber-600" />}
    </button>
  );
};

export default ThemeSelector;
